import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { useContext, useEffect, useState, useRef } from 'react'
import { IconButton, InputAdornment, TextField } from '@mui/material'

import Chip from '../common/chip'
import { FormError } from '../../types'
import { numberWithCommas } from '../../utils'
import { requestAccessToken } from '../../api/auth.api'
import HashIcon from '../../assets/icons/hash-icon.svg'
import SearchIcon from '../../assets/icons/search-icon.svg'
import TicketIcon from '../../assets/icons/ticket-icon.svg'
import { textFieldWhiteBg } from '../../theme/custom-styles'
import { handleIsValidMatch } from '../../utils/common.utils'
import { AppStateContext, AuthContext } from '../../contexts'
import CalendarIcon from '../../assets/icons/calendar-icon.svg'
import DownloadIcon from '../../assets/icons/download-icon.svg'
import { getLastestBatchesByCompany } from '../../api/payments.api'
import CheckIconGreen from '../../assets/icons/check-icon-green.svg'
import MoneyGreenIcon from '../../assets/icons/money-icon-green.svg'
import { bankingPartnerLogos, countriesCurrencies } from '../../lib/common.lib'
import { PaymentsHistoryModal } from '../insights-details-management/payments-history-modal'
import { BankBalanceSquareContainer } from '../banks-balances-management/bank-balance-square-container'

export const ReconciliationBriefModule = ({
  customerCompanyProfile
}: {
  customerCompanyProfile?: { balance: string; businessName: string; businessDocumentNumber: string; businessEmail: string; businessOwner: string }
}) => {
  const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'
  const currency = (countriesCurrencies as { [key: string]: string })[selectedCountry].toLowerCase()

  const navigate = useNavigate()
  const { customerCompanyId } = useParams()
  const lastestBatchesDataRef = useRef<Array<any>>([])
  const { isAuth, removeAuth } = useContext(AuthContext)
  const { banksBalancesDataLoaded, bankingPartnersBalanceByCountry } = useContext(AppStateContext)
  const { t } = useTranslation(['customer-company-details-management', 'documents-completed-management', 'system-users-management'])

  const [errors, setErrors] = useState<FormError>({})
  const [batchToSearch, setBatchToSearch] = useState<string>('')
  const [loadingLocalData, setLoadingLocalData] = useState<boolean>(true)
  const [lastestBatchesData, setLastestBatchesData] = useState<Array<any>>([])
  const [openPaymentsHistoryModal, setOpenPaymentsHistoryModal] = useState<boolean>(false)

  useEffect(() => {
    handleGetComponentData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleGetComponentData = async () => {
    try {
      const {
        data: { accessToken }
      } = await requestAccessToken(String(isAuth).replaceAll('"', ''))

      handleGetLastestBatchesByCompany(accessToken, customerCompanyId)
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      }
    }
  }

  const handleGetLastestBatchesByCompany = async (accessToken: string, customerCompanyId: string | undefined) => {
    if (!customerCompanyId) return

    try {
      const {
        data: { currentLastestBatchesData }
      } = await getLastestBatchesByCompany(customerCompanyId, accessToken)

      lastestBatchesDataRef.current = currentLastestBatchesData
      setLastestBatchesData(currentLastestBatchesData)
      setLoadingLocalData(false)
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      }
    }
  }

  const handleSetBatchesDataToSearch = (batchToSearchData: string, showError: boolean) => {
    setErrors({})
    setBatchToSearch(batchToSearchData)
    const currentFullBatchesData = [...(lastestBatchesDataRef.current as Array<any>)]

    if (batchToSearchData === '') {
      setLastestBatchesData(currentFullBatchesData)

      if (showError) setErrors({ batchToSearch: t('searchField.errors.emptyField', { ns: 'documents-ready-management' }) })
      return
    }

    const filteredBatches = currentFullBatchesData.filter((batchData) => {
      const batchId = handleIsValidMatch(batchToSearchData.toLowerCase(), String(batchData._id).slice(-8))
      const batchStatusText = handleIsValidMatch(batchToSearchData.toLowerCase(), String(handleGetBatchChipStatusText(batchData.status)).toLowerCase())
      const batchName = handleIsValidMatch(batchToSearchData.toLowerCase(), batchData.originalName.replace(/\.[^.]*$/, '').toLowerCase())
      const batchDate = handleIsValidMatch(batchToSearchData.toLowerCase(), String(format(new Date(batchData.createdAt), 'dd/MM/yyyy | HH:mm:ss')).toLowerCase())
      const batchNumPayments = handleIsValidMatch(batchToSearchData.toLowerCase(), String(batchData.totalOfNoveltiesReviewed).toLowerCase())
      const batchBankingPartner = handleIsValidMatch(
        batchToSearchData.toLowerCase(),
        batchData.cobrePaymentReference && Array.isArray(batchData.cobrePaymentReference) && batchData.cobrePaymentReference.length > 0 ? 'cobre' : 'monnet'
      )
      const batchAmount = handleIsValidMatch(batchToSearchData.toLowerCase(), `${batchData.batchAmount} cop`)

      return batchId || batchStatusText || batchName || batchDate || batchNumPayments || batchBankingPartner || batchAmount
    })

    setLastestBatchesData(filteredBatches)
  }

  const handleGetBatchChipStatusText = (batchStatus: string) => {
    if (batchStatus === 'uploaded' || batchStatus === 'analyzing' || batchStatus === 'downloaded' || batchStatus === 'downloaded-n-analyzing') {
      return t('listItem.chipStatusTexts.analyzing', { ns: 'documents-to-review-management' })
    }

    if (batchStatus === 'error-downloading-file') {
      return t('listItem.chipStatusTexts.downloadError', { ns: 'documents-to-review-management' })
    }

    if (batchStatus === 'error-analyzing-file') {
      return t('listItem.chipStatusTexts.fileError', { ns: 'documents-to-review-management' })
    }

    if (batchStatus === 'stopped') {
      return t('listItem.chipStatusTexts.stoppedAnalysis', { ns: 'documents-to-review-management' })
    }

    if (batchStatus === 'error') {
      return t('listItem.chipStatusTexts.cobreError', { ns: 'documents-to-review-management' })
    }

    if (batchStatus === 'analysis-completed') {
      return t('listItem.chipStatusTexts.completedAnalysis', { ns: 'documents-to-review-management' })
    }

    if (batchStatus === 'ready-for-disperse') {
      return t('listItem.chipStatusTexts.readyForDisperse', { ns: 'documents-completed-management' })
    }

    if (batchStatus === 'dispersing' || batchStatus === 'register-disperse-completed') {
      return t('listItem.chipStatusTexts.dispersing', { ns: 'documents-completed-management' })
    }

    if (batchStatus === 'completed') {
      return t('listItem.chipStatusTexts.completed', { ns: 'documents-completed-management' })
    }

    return t('listItem.chipStatusTexts.inProcess', { ns: 'documents-to-review-management' })
  }

  const handleCloseSession = () => {
    removeAuth?.()
    navigate('/', { replace: true, state: { sessionExpired: true } })
  }

  return (
    <div style={{ marginTop: '24px' }}>
      <div
        style={{ minHeight: 'unset' }}
        className="bank-balances-container"
      >
        <div className="banks-balances-squares-containers">
          {bankingPartnersBalanceByCountry?.map((bankingPartnerData) => (
            <BankBalanceSquareContainer
              hideBalanceData
              hideSecondaryData
              key={bankingPartnerData.name}
              dataLoaded={banksBalancesDataLoaded}
              mainData={`$ ${numberWithCommas(Number(bankingPartnerData.balance))} ${currency}`}
              logoImage={(bankingPartnerLogos as { [key: string]: string })[bankingPartnerData.bdValue]}
              handleRedirectToBankingPartnerDetails={() =>
                navigate(`/banking-partner-details/${bankingPartnerData.bdValue}`, {
                  state: { bankingPartnerName: bankingPartnerData.name.toUpperCase(), companyName: customerCompanyProfile?.businessName.toUpperCase(), previousPath: `customer-company-details/${customerCompanyId}` }
                })
              }
            />
          ))}
        </div>
      </div>
      <div
        style={{ marginTop: '24px' }}
        className="customer-company-fundings-history-container"
      >
        <div className="actions-bar">
          <div className="left-content">
            <div className="title">{lastestBatchesData.length === 0 ? t('actionsBar.thereIsNoBatchesToShow') : t('actionsBar.latestBatches')}</div>
            {!loadingLocalData && lastestBatchesData.length > 0 && (
              <div
                style={{ cursor: 'pointer' }}
                className="filter-button-container"
                onClick={() => setOpenPaymentsHistoryModal(true)}
              >
                <img
                  src={DownloadIcon}
                  alt="download-icon"
                />
                <div className="filter-button-text">{t('actionsBar.downloadReport', { ns: 'customer-details-management' })}</div>
              </div>
            )}
          </div>
          <TextField
            variant="standard"
            sx={textFieldWhiteBg}
            value={batchToSearch}
            className="search-input"
            error={!!errors.batchToSearch}
            helperText={errors.batchToSearch}
            label={t('searchField.paymentPlaceholder')}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                handleSetBatchesDataToSearch(batchToSearch, true)
              }
            }}
            onChange={(event) => handleSetBatchesDataToSearch(event.target.value, true)}
            onBlur={() => {
              if (batchToSearch === '') setErrors({})
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    edge="end"
                    aria-label="toggle password visibility"
                    sx={{ color: '#107569', right: '10px', bottom: '3px' }}
                  >
                    <img
                      src={SearchIcon}
                      alt="search-icon"
                    />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        </div>
      </div>
      {!loadingLocalData && lastestBatchesData.length > 0 && (
        <>
          <div className="table-header-container table-header-batches">
            <div
              id="last-batches-header-date"
              className="text-header table-header-width-15"
            >
              {t('lastBatches.tableHeaders.date', { ns: 'reports-management' }).toUpperCase()}
            </div>
            <div
              id="last-batches-header-name"
              className="text-header table-header-width-15"
            >
              {t('lastBatches.tableHeaders.nameOfOperation', { ns: 'reports-management' }).toUpperCase()}
            </div>
            <div
              id="last-batches-header-id"
              className="text-header table-header-width-15"
            >
              {t('lastBatches.tableHeaders.batchId', { ns: 'reports-management' }).toUpperCase()}
            </div>
            <div
              id="last-batches-header-partner"
              className="text-header table-header-width-15"
            >
              {t('lastBatches.tableHeaders.bankingPartner', { ns: 'reports-management' }).toUpperCase()}
            </div>
            <div
              id="last-batches-header-num-payments"
              className="text-header table-header-width-15"
            >
              {t('lastBatches.tableHeaders.numberOfPayments', { ns: 'reports-management' }).toUpperCase()}
            </div>
            <div
              id="last-batches-header-amount"
              className="text-header table-header-width-15"
            >
              {t('lastBatches.tableHeaders.amount', { ns: 'reports-management' }).toUpperCase()}
            </div>
            <div
              id="last-batches-header-status"
              className="text-header table-header-width-15"
            >
              {t('lastBatches.tableHeaders.status', { ns: 'reports-management' }).toUpperCase()}
            </div>
          </div>
          <div className="documents-list-container">
            {lastestBatchesData.map((batchData, index) => {
              const batchStatusText = handleGetBatchChipStatusText(batchData.status)

              return (
                <div
                  key={index}
                  className="customer-details-list-item-data-container"
                >
                  <div className="customer-company-table-column last-batches-table-width-15 max-width-unset">
                    <div className="customer-details-list-item-icon">
                      <img
                        src={CalendarIcon}
                        alt="calendar-icon"
                      />
                    </div>
                    <div className="customer-details-list-item-text">{format(new Date(batchData.createdAt), 'dd/MM/yyyy | HH:mm:ss')}</div>
                  </div>
                  <div className="customer-company-table-column last-batches-table-width-15 max-width-unset">
                    <div className="customer-details-list-item-text">{batchData.originalName.replace(/\.[^.]*$/, '').toUpperCase()}</div>
                  </div>
                  <div className="customer-company-table-column last-batches-table-width-15 max-width-unset">
                    <div className="customer-details-list-item-icon">
                      <img
                        src={TicketIcon}
                        alt="ticket-icon"
                      />
                    </div>
                    <div className="customer-details-list-item-text">{String(batchData._id).slice(-8)}</div>
                  </div>
                  <div className="customer-company-table-column last-batches-table-width-15 max-width-unset">
                    <div className="customer-details-list-item-icon">
                      <img
                        src={CheckIconGreen}
                        alt="check-icon-green"
                      />
                    </div>
                    <div className="customer-details-list-item-text">
                      {batchData.cobrePaymentReference && Array.isArray(batchData.cobrePaymentReference) && batchData.cobrePaymentReference.length > 0 ? 'COBRE' : 'MONNET'}
                    </div>
                  </div>
                  <div className="customer-company-table-column last-batches-table-width-15 max-width-unset">
                    <div className="customer-details-list-item-icon">
                      <img
                        src={HashIcon}
                        alt="hash-icon"
                      />
                    </div>
                    <div className="customer-details-list-item-text">{batchData.totalOfNoveltiesReviewed}</div>
                  </div>
                  <div className="customer-company-table-column last-batches-table-width-15 max-width-unset">
                    <div className="customer-details-list-item-icon">
                      <img
                        src={MoneyGreenIcon}
                        alt="money-icon"
                      />
                    </div>
                    <div className="customer-details-list-item-text">{`${numberWithCommas(batchData.batchAmount)} COP`}</div>
                  </div>
                  <div className="customer-company-table-column last-batches-table-width-15 max-width-unset">
                    <Chip
                      status={batchData.status}
                      label={batchStatusText}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        </>
      )}
      {openPaymentsHistoryModal && (
        <PaymentsHistoryModal
          handleSetModalOpenState={(openState: boolean) => {
            setOpenPaymentsHistoryModal(openState)
          }}
          filtersFromOutside={{
            reportType: 'batches',
            belongsTo: String(customerCompanyId)
          }}
          customerCompanyPaymentsData={[]}
        />
      )}
    </div>
  )
}
